const VERSION_STORAGE_KEY = 'ubikalo::assetManifestVersion';
const BUILD_PARAM = 'build';

const hasWindow = typeof window !== 'undefined';

const getManifestUrl = () => {
  const cacheBuster = Date.now().toString();

  if (hasWindow) {
    const url = new URL('asset-manifest.json', window.location.origin);
    url.searchParams.set('cache-bust', cacheBuster);
    return url.toString();
  }

  const baseUrl = process.env.PUBLIC_URL || '';
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${normalizedBase}asset-manifest.json?cache-bust=${cacheBuster}`;
};

const getStoredVersion = () => {
  if (!hasWindow) {
    return null;
  }
  try {
    return window.localStorage.getItem(VERSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeVersion = (value) => {
  if (!hasWindow) {
    return;
  }
  try {
    window.localStorage.setItem(VERSION_STORAGE_KEY, value);
  } catch {
    // Ignored: storage might be unavailable (private mode, etc.)
  }
};

const extractVersionFromManifest = (manifest) => {
  if (!manifest) {
    return null;
  }

  const tokens = new Set();

  if (manifest.files) {
    const criticalAssets = ['main.js', 'main.css'];
    criticalAssets.forEach((asset) => {
      if (manifest.files[asset]) {
        tokens.add(manifest.files[asset]);
      }
    });

    Object.entries(manifest.files).forEach(([key, value]) => {
      if (/^static\/(js|css)\//.test(key)) {
        tokens.add(value || key);
      }
    });
  }

  if (Array.isArray(manifest.entrypoints)) {
    manifest.entrypoints.forEach((entry) => tokens.add(entry));
  }

  if (!tokens.size) {
    return null;
  }

  return Array.from(tokens)
    .filter(Boolean)
    .sort()
    .join('|');
};

const purgeServiceWorkers = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
};

const purgeCaches = async () => {
  if (!('caches' in window)) {
    return;
  }
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
};

const forceReload = (versionTag) => {
  const url = new URL(window.location.href);
  url.searchParams.set(BUILD_PARAM, versionTag);
  window.location.replace(url.toString());
};

export async function ensureFreshBuild() {
  if (!hasWindow || process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (typeof fetch === 'undefined') {
    return true;
  }

  let timeoutId;
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) {
      timeoutId = setTimeout(() => {
        try { controller.abort(); } catch (e) {}
      }, 5000);
    }
    const response = await fetch(getManifestUrl(), {
      cache: 'no-store',
      signal: controller ? controller.signal : undefined,
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (!response.ok) {
      return true;
    }

    const manifest = await response.json();
    const manifestVersion = extractVersionFromManifest(manifest);
    const headerVersion = response.headers.get('etag') || response.headers.get('last-modified');
    const latestVersion = [manifestVersion, headerVersion].filter(Boolean).join('|');

    if (!latestVersion) {
      return true;
    }

    const storedVersion = getStoredVersion();

    if (!storedVersion) {
      storeVersion(latestVersion);
      return true;
    }

    if (storedVersion === latestVersion) {
      return true;
    }

    storeVersion(latestVersion);
    await purgeServiceWorkers();
    await purgeCaches();
    forceReload(latestVersion);
    return false;
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    console.warn('No se pudo validar la versión del build:', error);
    return true;
  }
}
